import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Shield, Bell, ChevronDown, LogOut, Settings, Wallet, LayoutDashboard } from 'lucide-react'
import { cn } from '../lib/utils'
import { useAuth } from '../auth/useAuth'
import { GlassButton } from '../components/ui/glass'

const MARKETING_LINKS = [
  { label: 'Home',     path: '/'         },
  { label: 'Features', path: '/features' },
  { label: 'Compare',  path: '/compare'  },
  { label: 'Pricing',  path: '/pricing'  },
  { label: 'Docs',     path: '/docs'     },
  { label: 'Blog',     path: '/blog'     },
  { label: 'About',    path: '/about'    },
]

const CHAINS = [
  { id: 'eth', label: 'Ethereum', short: 'ETH' },
  { id: 'arb', label: 'Arbitrum', short: 'ARB' },
  { id: 'base', label: 'Base', short: 'BASE' },
  { id: 'polygon', label: 'Polygon', short: 'MATIC' },
  { id: 'sol', label: 'Solana', short: 'SOL' },
]

export function TopNav() {
  const { isAuthenticated, user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [chainMenuOpen, setChainMenuOpen] = useState(false)
  const [activeChain, setActiveChain] = useState(CHAINS[0])

  const handleLogout = () => {
    logout()
    setUserMenuOpen(false)
    navigate('/login')
  }

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-16 backdrop-blur-xl bg-white/[0.04] border-b border-white/[0.08]">
      <div className="max-w-7xl mx-auto h-full px-4 flex items-center gap-8">
        {/* Logo */}
        <Link to={isAuthenticated ? '/dashboard' : '/'} className="flex items-center gap-2 shrink-0">
          <div className="w-8 h-8 rounded-lg bg-gradient-brand flex items-center justify-center shadow-glow-sky">
            <Shield className="w-4 h-4 text-white" />
          </div>
          <span className="font-display font-bold text-lg tracking-wide text-white">
            MEV<span className="text-primary">Shield</span>
          </span>
        </Link>

        {/* Nav Links — always show marketing nav */}
        <nav className="flex-1 flex items-center gap-1">
          {MARKETING_LINKS.map(({ label, path }) => (
            <Link
              key={path}
              to={path}
              className={cn(
                'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                isActive(path)
                  ? 'bg-white/[0.10] text-white'
                  : 'text-white/60 hover:text-white hover:bg-white/[0.06]'
              )}
            >
              {label}
            </Link>
          ))}
        </nav>

        {/* Right Side */}
        <div className="flex items-center gap-3 shrink-0">
          {isAuthenticated ? (
            <>
              {/* Chain Selector */}
              <div className="relative">
                <button
                  onClick={() => { setChainMenuOpen(!chainMenuOpen); setUserMenuOpen(false) }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.06] border border-white/10 text-sm text-white/80 hover:text-white hover:bg-white/[0.10] transition-all"
                >
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  {activeChain.short}
                  <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                </button>
                {chainMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-44 backdrop-blur-xl bg-surface-panel/90 border border-white/10 rounded-xl shadow-glass overflow-hidden">
                    {CHAINS.map(chain => (
                      <button
                        key={chain.id}
                        onClick={() => { setActiveChain(chain); setChainMenuOpen(false) }}
                        className={cn(
                          'w-full px-4 py-2.5 text-sm text-left flex items-center gap-2 transition-colors',
                          activeChain.id === chain.id
                            ? 'text-primary bg-primary/10'
                            : 'text-white/70 hover:text-white hover:bg-white/[0.06]'
                        )}
                      >
                        {activeChain.id === chain.id && <span className="w-1.5 h-1.5 rounded-full bg-primary" />}
                        {activeChain.id !== chain.id && <span className="w-1.5 h-1.5" />}
                        {chain.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Notifications */}
              <button className="relative p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/[0.06] transition-all">
                <Bell className="w-4.5 h-4.5" />
              </button>

              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => { setUserMenuOpen(!userMenuOpen); setChainMenuOpen(false) }}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.06] border border-white/10 text-sm text-white hover:bg-white/[0.10] transition-all"
                >
                  <div className="w-6 h-6 rounded-full bg-gradient-brand flex items-center justify-center text-xs font-bold">
                    {user?.email?.[0]?.toUpperCase() ?? 'U'}
                  </div>
                  <span className="text-white/80">{user?.role}</span>
                  <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-52 backdrop-blur-xl bg-surface-panel/90 border border-white/10 rounded-xl shadow-glass overflow-hidden">
                    <div className="px-4 py-3 border-b border-white/[0.06]">
                      <p className="text-sm text-white font-medium truncate">{user?.email}</p>
                      <p className="text-xs text-white/50 mt-0.5">{user?.role}</p>
                    </div>
                    <Link to="/dashboard" onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/[0.06] transition-colors">
                      <LayoutDashboard className="w-4 h-4" /> Dashboard
                    </Link>
                    <Link to="/protection" onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/[0.06] transition-colors">
                      <Shield className="w-4 h-4" /> Protection
                    </Link>
                    <div className="border-t border-white/[0.06] mt-1 mb-1" />
                    <Link to="/settings" onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/[0.06] transition-colors">
                      <Settings className="w-4 h-4" /> Settings
                    </Link>
                    <Link to="/wallet" onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/[0.06] transition-colors">
                      <Wallet className="w-4 h-4" /> Wallet
                    </Link>
                    <div className="border-t border-white/[0.06]">
                      <button onClick={handleLogout}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors">
                        <LogOut className="w-4 h-4" /> Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/login">
                <GlassButton variant="ghost" size="sm">Login</GlassButton>
              </Link>
              <Link to="/signup">
                <GlassButton variant="primary" size="sm">Get Started →</GlassButton>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Close dropdowns on outside click */}
      {(userMenuOpen || chainMenuOpen) && (
        <div className="fixed inset-0 z-[-1]" onClick={() => { setUserMenuOpen(false); setChainMenuOpen(false) }} />
      )}
    </header>
  )
}
